import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/shared/card';
import { Button } from '@/components/shared/button';
import { Progress } from '@/components/shared/progress';
import { ROUTES } from '@/config/routes';
import { supabase } from '@/lib/supabase';

type ProcessStage = {
  name: string;
  metrics: {
    monthlyVolume: number | null;
    staffFte: number | null;
    processingTime: number | null;
    customerSatisfaction: number | null;
    digitalAdoption: number | null;
    firstContactResolution: number | null;
  };
};

const PermitBenchmarkingWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<{
    jurisdiction: string;
    stages: ProcessStage[];
  }>({
    jurisdiction: '',
    stages: [
      {
        name: 'Pre-Application',
        metrics: {
          monthlyVolume: null,
          staffFte: null,
          processingTime: null,
          customerSatisfaction: null,
          digitalAdoption: null,
          firstContactResolution: null,
        },
      },
      {
        name: 'Application Submission',
        metrics: {
          monthlyVolume: null,
          staffFte: null,
          processingTime: null,
          customerSatisfaction: null,
          digitalAdoption: null,
          firstContactResolution: null,
        },
      },
      {
        name: 'Plan Review',
        metrics: {
          monthlyVolume: null,
          staffFte: null,
          processingTime: null,
          customerSatisfaction: null,
          digitalAdoption: null,
          firstContactResolution: null,
        },
      },
      {
        name: 'Inspections',
        metrics: {
          monthlyVolume: null,
          staffFte: null,
          processingTime: null,
          customerSatisfaction: null,
          digitalAdoption: null,
          firstContactResolution: null,
        },
      },
    ],
  });

  const totalSteps = formData.stages.length + 2; // +2 for jurisdiction and review steps

  const handleJurisdictionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      jurisdiction: e.target.value,
    }));
  };

  const handleMetricChange = (
    stageIndex: number,
    metricName: keyof ProcessStage['metrics'],
    value: string
  ) => {
    setFormData((prev) => {
      const newStages = [...prev.stages];
      newStages[stageIndex] = {
        ...newStages[stageIndex],
        metrics: {
          ...newStages[stageIndex].metrics,
          [metricName]: value === '' ? null : parseFloat(value),
        },
      };
      return {
        ...prev,
        stages: newStages,
      };
    });
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      const { error } = await supabase.from('process_assessments').insert([
        {
          jurisdiction: formData.jurisdiction,
          stages: formData.stages,
        },
      ]);

      if (error) throw error;

      navigate(ROUTES.PERMIT_BENCHMARKING.ROOT);
    } catch (error) {
      console.error('Error submitting assessment:', error);
    }
  };

  const renderJurisdictionStep = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Enter Your Jurisdiction</h2>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Jurisdiction Name
        </label>
        <input
          type="text"
          value={formData.jurisdiction}
          onChange={handleJurisdictionChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="e.g., City of Example"
        />
      </div>
    </div>
  );

  const renderProcessStageStep = (stageIndex: number) => {
    const stage = formData.stages[stageIndex];
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">{stage.name}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(stage.metrics).map(([key, value]) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700">
                {key
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, (str) => str.toUpperCase())}
              </label>
              <input
                type="number"
                value={value === null ? '' : value}
                onChange={(e) =>
                  handleMetricChange(
                    stageIndex,
                    key as keyof ProcessStage['metrics'],
                    e.target.value
                  )
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderReviewStep = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Review Your Submission</h2>
      <div>
        <h3 className="text-lg font-semibold">Jurisdiction</h3>
        <p>{formData.jurisdiction}</p>
      </div>
      {formData.stages.map((stage, index) => (
        <div key={index}>
          <h3 className="text-lg font-semibold">{stage.name}</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(stage.metrics).map(([key, value]) => (
              <div key={key}>
                <span className="font-medium">
                  {key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase())}
                  :{' '}
                </span>
                <span>{value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <Card className="p-6">
        <div className="mb-8">
          <Progress value={(currentStep / totalSteps) * 100} />
          <div className="mt-2 text-sm text-gray-500">
            Step {currentStep} of {totalSteps}
          </div>
        </div>

        {currentStep === 1 && renderJurisdictionStep()}
        {currentStep > 1 &&
          currentStep <= formData.stages.length + 1 &&
          renderProcessStageStep(currentStep - 2)}
        {currentStep === totalSteps && renderReviewStep()}

        <div className="mt-8 flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            Back
          </Button>
          {currentStep === totalSteps ? (
            <Button onClick={handleSubmit}>Submit</Button>
          ) : (
            <Button onClick={handleNext}>Next</Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PermitBenchmarkingWizard; 